import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, createVersionForUpload, getAdminToken, setAdminToken, uploadSource } from '../api.ts';
import type { Playlist, PlaylistItem, Project, SheetStatusLabels, Version, VersionStatus } from '../types.ts';
import { formatBytes } from '../format.ts';
import { aggregateStatus, compareVersions, naturalCompare, parseShotVersion } from '../naming.ts';
import { SHOT_STATES, shotState } from '../shotState.ts';
import { SheetPanel } from './SheetPanel.tsx';

type ViewMode = 'grid' | 'list';

/** A view choice remembered per browser; storage can be unavailable (private mode), so guarded. */
function usePersistedView(key: string): [ViewMode, (view: ViewMode) => void] {
  const [view, setView] = useState<ViewMode>(() => {
    try {
      return localStorage.getItem(key) === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  });
  const change = (next: ViewMode): void => {
    setView(next);
    try {
      localStorage.setItem(key, next);
    } catch {
      // Remembering is a nicety; the switch itself still works.
    }
  };
  return [view, change];
}

/** An on/off choice remembered per browser, on unless switched off. */
function usePersistedFlag(key: string): [boolean, () => void] {
  const [on, setOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem(key) !== '0';
    } catch {
      return true;
    }
  });
  const toggle = (): void => {
    setOn((prev) => {
      try {
        localStorage.setItem(key, prev ? '0' : '1');
      } catch {
        // Remembering is a nicety; the switch itself still works.
      }
      return !prev;
    });
  };
  return [on, toggle];
}

/** Grid/list switch as two icon buttons — four tiles, three lines. */
function ViewToggle({
  view,
  onChange,
  label,
}: {
  view: ViewMode;
  onChange: (view: ViewMode) => void;
  label: string;
}): React.JSX.Element {
  return (
    <span className="view-toggle" role="group" aria-label={label}>
      <button
        type="button"
        className={view === 'grid' ? 'active' : ''}
        aria-pressed={view === 'grid'}
        aria-label="Grid view"
        title="Grid view"
        onClick={() => onChange('grid')}
      >
        <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="4" width="7" height="7" rx="1" />
          <rect x="13" y="4" width="7" height="7" rx="1" />
          <rect x="4" y="13" width="7" height="7" rx="1" />
          <rect x="13" y="13" width="7" height="7" rx="1" />
        </svg>
      </button>
      <button
        type="button"
        className={view === 'list' ? 'active' : ''}
        aria-pressed={view === 'list'}
        aria-label="List view"
        title="List view"
        onClick={() => onChange('list')}
      >
        <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
    </span>
  );
}

interface UploadState {
  filename: string;
  progress: number;
  /** 'skipped' = that version is already in the project, so the file was not sent again.
   *  'queued' = waiting for the files before it — uploads go one after the other. */
  state: 'queued' | 'uploading' | 'transcoding' | 'done' | 'skipped' | 'error';
  message?: string;
}

/** What GET /api/v1/storage answers — the disk report for DATA_DIR. */
type StorageReport = Awaited<ReturnType<typeof api.storage>>;

// Also matches PNG/JPG: the server ingests a still image as a short looped clip, so it is
// uploaded and filed exactly like any other clip.
const VIDEO_EXT = /\.(mov|mp4|mxf|mkv|m4v|png|jpe?g)$/i;

/** dd-mm-yyyy — locale-independent, unlike toLocaleDateString, so the default name never shifts. */
function todayDdMmYyyy(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/**
 * A file to upload, paired with the name of the top-level folder it came from — that name is
 * what the default review is called, so a folder called "Dailies 2026-01-15" produces a review
 * of the same name instead of the generic "dd-mm-yyyy_VFX-review".
 */
export interface DroppedFile {
  file: File;
  folder: string | null;
}

/**
 * Collect files from a drop, descending into any dropped folders. Falls back to the flat
 * file list on browsers without the directory-entry API.
 */
async function filesFromDrop(dt: DataTransfer): Promise<DroppedFile[]> {
  const items = Array.from(dt.items ?? []);
  const roots = items
    .map((it) => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
    .filter((e): e is FileSystemEntry => e !== null);
  if (roots.length === 0) return Array.from(dt.files).map((file) => ({ file, folder: null }));

  const out: DroppedFile[] = [];
  const walk = async (entry: FileSystemEntry, folder: string | null): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((res, rej) =>
        (entry as FileSystemFileEntry).file(res, rej),
      );
      out.push({ file, folder });
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        if (batch.length === 0) break;
        for (const child of batch) await walk(child, folder);
      }
    }
  };
  // A dropped folder passes its own name to everything below it; a bare file has no folder.
  for (const root of roots) await walk(root, root.isDirectory ? root.name : null);
  return out;
}

/** Wrap a plain file list, deriving the folder from webkitRelativePath if the browser set one. */
function fromFileList(files: FileList): DroppedFile[] {
  return Array.from(files).map((file) => {
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
    const folder = rel.includes('/') ? rel.split('/')[0]! : null;
    return { file, folder };
  });
}

/** The folder shared by every file, or null if they came from more than one place. */
function commonFolder(items: DroppedFile[]): string | null {
  const folders = new Set(items.map((i) => i.folder).filter((f): f is string => f !== null));
  return folders.size === 1 ? [...folders][0]! : null;
}

/**
 * Management side: projects, shots, uploads, playlists, session links.
 * Everything here goes through the same public API a pipeline client would use.
 */
export function AdminPage(): React.JSX.Element {
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [playlists, setPlaylists] = useState<(Playlist & { shotCount: number })[]>([]);
  // A "clip" is a shot: selection and deletion work per shot, not per version.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Which version of each shot is currently shown/acted on (default: the highest).
  const [chosenVersion, setChosenVersion] = useState<Record<string, string>>({});
  // Grid of poster thumbnails, or a compact list — remembered per browser, like the sheet panel.
  const [clipView, changeClipView] = usePersistedView('openreview.clipView');
  const [projectView, changeProjectView] = usePersistedView('openreview.projectView');
  // What each clip shows besides its name: the version picker and the status dot.
  const [showVersions, toggleShowVersions] = usePersistedFlag('openreview.clipShowVersions');
  const [showStatus, toggleShowStatus] = usePersistedFlag('openreview.clipShowStatus');
  // The review whose membership is being edited: clicking it below pre-checks its clips, so
  // unchecking one and hitting "Update review" removes it. Holds the items as they were when
  // editing started, so "Update" can diff against them without a second fetch.
  const [editingReview, setEditingReview] = useState<{ id: string; name: string; items: PlaylistItem[] } | null>(
    null,
  );
  // A long press on a review's name renames it in place; set on press-and-hold, cleared on save/cancel.
  const [renamingReviewId, setRenamingReviewId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const [uploads, setUploads] = useState<Record<string, UploadState>>({});
  // Per file being uploaded: how to stop it, and what it already became on the server.
  const uploadControl = useRef(
    new Map<string, { cancelled: boolean; abort?: AbortController; versionId?: string }>(),
  );
  // The folder the newest upload came from. The default review name uses it, so a drag of
  // "Dailies_2026-01-15/" becomes a review of that name instead of the generic date one.
  const [suggestedReviewName, setSuggestedReviewName] = useState<string | null>(null);
  const [storage, setStorage] = useState<StorageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(getAdminToken());
  const [authRequired, setAuthRequired] = useState(false);
  // The management API is admin-only; until a valid key is in place we show only the lock.
  const [locked, setLocked] = useState(false);
  const [lockTried, setLockTried] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);

  const refreshProject = useCallback(async (id: string) => {
    const res = await api.getProject(id);
    setVersions(res.versions);
    const pl = await api.listPlaylists(id);
    setPlaylists(pl.playlists);
  }, []);

  const loadStorage = useCallback(async () => {
    try {
      setStorage(await api.storage());
    } catch {
      // The disk report is a nicety — losing it must not take the management screens with it.
      setStorage(null);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const res = await api.listProjects();
      setLocked(false);
      setProjects(res.projects);
      void loadStorage();
      // Deep link: /?project=<id> opens straight into that project (used by the review page).
      const wanted = new URLSearchParams(location.search).get('project');
      if (wanted && res.projects.some((p) => p.id === wanted)) {
        setProjectId(wanted);
        await refreshProject(wanted);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setLocked(true);
      else setError(err instanceof Error ? err.message : String(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshProject, loadStorage]);

  useEffect(() => {
    void api.health().then((h) => setAuthRequired(h.authRequired));
    void loadProjects();
    // Intentionally runs once: later refreshes are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The linked sheet, if any: the words its status dropdown offers and its vocabulary for the
  // verdicts. Null for a project without a sheet — the dot then offers the plain verdicts.
  const [sheet, setSheet] = useState<{ options: string[]; labels: SheetStatusLabels } | null>(null);

  /**
   * Keeping the project view in step with its Google Sheet: read the sheet back the moment the
   * project opens (dropdown words included), then again every few seconds while it stays open
   * and the tab is visible. Changes made here go out immediately through the server's outbox, so
   * between the two the view and the sheet are never more than a few seconds apart.
   */
  useEffect(() => {
    setSheet(null);
    if (!projectId) return;
    let cancelled = false;
    let busy = false;
    const pull = async (withOptions: boolean): Promise<void> => {
      // The repeat pulls pause while the tab is in the background; the first one never does.
      if (busy || (!withOptions && document.hidden)) return;
      busy = true;
      try {
        const res = await api.pullSheet(projectId, withOptions);
        if (cancelled) return;
        if (res.linked) {
          if (withOptions) setSheet({ options: res.statusOptions, labels: res.statusLabels });
          await refreshProject(projectId);
        }
      } catch {
        // A missed pull is caught up by the next one; nothing to tell the user about.
      } finally {
        busy = false;
      }
    };
    void pull(true);
    const timer = window.setInterval(() => void pull(false), 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [projectId, refreshProject]);

  // While anything is transcoding, poll so the list stops saying "processing" on its own.
  useEffect(() => {
    if (!projectId) return;
    const pending = versions.some((v) => v.ingestState === 'processing' || v.ingestState === 'queued');
    if (!pending) return;
    const timer = window.setInterval(() => void refreshProject(projectId).catch(() => undefined), 2000);
    return () => window.clearInterval(timer);
  }, [projectId, versions, refreshProject]);

  // Group versions into clips (one entry per shot), sorted naturally by shot name so the reader
  // finds SHOT_010 above SHOT_020 and SHOT_100 below SHOT_020, no matter the upload order. Within
  // a shot the versions are sorted ascending by version number, so the last one is the highest
  // — by name it would not be: v9 sorts after v100.
  const shotGroups = useMemo(() => {
    const map = new Map<string, Version[]>();
    for (const v of versions) {
      const arr = map.get(v.shotId);
      if (arr) arr.push(v);
      else map.set(v.shotId, [v]);
    }
    for (const group of map.values()) group.sort(compareVersions);
    return new Map(
      [...map.entries()].sort(([, a], [, b]) => naturalCompare(a[0]!.shotName, b[0]!.shotName)),
    );
  }, [versions]);

  // Default each shot's shown version to its highest; keep a still-valid manual choice.
  useEffect(() => {
    setChosenVersion((prev) => {
      const next: Record<string, string> = {};
      for (const [shotId, vs] of shotGroups) {
        const keep = prev[shotId];
        next[shotId] = keep && vs.some((v) => v.id === keep) ? keep : (vs[vs.length - 1]?.id ?? '');
      }
      return next;
    });
    // Drop selections whose shot has vanished.
    setSelected((prev) => new Set(Array.from(prev).filter((id) => shotGroups.has(id))));
  }, [shotGroups]);

  const chosenVersionOf = useCallback(
    (shotId: string): Version | null => {
      const vs = shotGroups.get(shotId);
      if (!vs || vs.length === 0) return null;
      return vs.find((v) => v.id === chosenVersion[shotId]) ?? vs[vs.length - 1] ?? null;
    },
    [shotGroups, chosenVersion],
  );

  /**
   * Selecting a clip is a click on the card itself — the highlighted ring it already shows is
   * the feedback, so a checkbox next to it was the same fact twice. A clip that is still
   * ingesting cannot go into a review, so it does not answer.
   */
  const toggleClip = useCallback((shotId: string, ready: boolean): void => {
    if (!ready) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(shotId)) next.delete(shotId);
      else next.add(shotId);
      return next;
    });
  }, []);

  const openProject = async (id: string): Promise<void> => {
    setProjectId(id);
    setSelected(new Set());
    setEditingReview(null);
    window.history.replaceState(null, '', `/?project=${id}`);
    await refreshProject(id);
  };

  const backToList = (): void => {
    setProjectId(null);
    setSelected(new Set());
    setEditingReview(null);
    window.history.replaceState(null, '', '/');
  };

  const createProject = async (): Promise<void> => {
    const name = prompt('Project name?');
    if (!name) return;
    try {
      const project = await api.createProject({ name });
      setProjects((prev) => [project, ...prev]);
      await openProject(project.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const deleteProject = async (p: Project): Promise<void> => {
    if (!confirm(`Delete project “${p.name}” and all of its clips? This cannot be undone.`)) return;
    try {
      await api.deleteProject(p.id);
      setProjects((prev) => prev.filter((x) => x.id !== p.id));
      if (projectId === p.id) backToList();
      void loadStorage();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Delete only the shown version of each selected clip; other versions of the shot survive.
  const deleteSelectedVersions = async (): Promise<void> => {
    const targets = Array.from(selected)
      .map((shotId) => chosenVersionOf(shotId))
      .filter((v): v is Version => v !== null);
    if (targets.length === 0) return;
    if (
      !confirm(
        `Delete ${targets.length} selected version(s)? Only the shown version of each clip is ` +
          `removed — other versions stay. This also deletes the original file and cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      for (const v of targets) await api.deleteVersion(v.id);
      setSelected(new Set());
      setEditingReview(null);
      if (projectId) await refreshProject(projectId);
      void loadStorage();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  /** Deletes the review that is currently open — the one with the highlighted border. */
  const deleteOpenReview = async (): Promise<void> => {
    if (!editingReview) return;
    if (!confirm(`Delete the review “${editingReview.name}” and its link?`)) return;
    try {
      await api.deletePlaylist(editingReview.id);
      setEditingReview(null);
      setSelected(new Set());
      if (projectId) await refreshProject(projectId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  /**
   * Whether the people holding this review's link may download its clips. Updated in place
   * rather than by refetching the project: the reviews list is the only thing that changed,
   * and a full refresh here would also rebuild the clips table under the cursor.
   */
  const toggleReviewDownloads = async (playlist: Playlist): Promise<void> => {
    const next = playlist.guest_downloads ? 0 : 1;
    try {
      await api.setPlaylistSettings(playlist.id, { guestDownloads: next === 1 });
      setPlaylists((prev) =>
        prev.map((p) => (p.id === playlist.id ? { ...p, guest_downloads: next } : p)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  /**
   * Quick fix from the project overview — mainly for undoing an approval made by mistake.
   * Targets the version the shown verdict actually came from (`aggregateStatus`'s highest
   * approved/rejected version), not necessarily the version the card happens to be showing.
   * Rejecting still needs a reason — the server refuses a reason-less one either way — so it is
   * asked for here instead of silently failing.
   */
  /**
   * The same from a project with a sheet: any word its status dropdown offers. The server lets
   * the verdicts follow the word and queues the sheet write; shown here at once, not after it.
   */
  const changeShotStage = async (shotId: string, word: string): Promise<void> => {
    let comment: string | undefined;
    if (sheet && word.trim().toLowerCase() === sheet.labels.rejected.trim().toLowerCase()) {
      const reason = window.prompt('Reason for rejecting this clip?')?.trim();
      if (!reason) return;
      comment = reason;
    }
    setVersions((prev) => prev.map((v) => (v.shotId === shotId ? { ...v, sheetStage: word } : v)));
    try {
      await api.setShotStage(shotId, word, comment);
      if (projectId) await refreshProject(projectId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      if (projectId) void refreshProject(projectId);
    }
  };

  const changeShotStatus = async (version: Version, status: VersionStatus): Promise<void> => {
    let comment: string | undefined;
    if (status === 'rejected') {
      const reason = window.prompt('Reason for rejecting this clip?')?.trim();
      if (!reason) return;
      comment = reason;
    }
    try {
      await api.setStatus(version.id, { status, comment });
      setVersions((prev) => prev.map((v) => (v.id === version.id ? { ...v, status } : v)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Drop an image onto a project tile to set its cover.
  const setThumbnail = async (id: string, file: File): Promise<void> => {
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('could not read image'));
        reader.readAsDataURL(file);
      });
      const updated = await api.setProjectThumbnail(id, dataUrl);
      setProjects((prev) => prev.map((p) => (p.id === id ? updated : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const currentProject = projects.find((p) => p.id === projectId) ?? null;

  /**
   * Upload flow: shot name and version number are derived from the filename
   * (FRW_145_010_v02.mov -> shot FRW_145_010, version v02) so versions of the same shot
   * group together. Non-video files (dropped alongside in a folder) are skipped.
   *
   * Uploading and transcoding are deliberately separate phases. ffmpeg is far slower than the
   * network, so waiting for each transcode before sending the next file would leave the line
   * idle for most of a folder drop. Everything goes up first; the queue is then watched as a
   * whole, and clips get pre-selected as they land.
   */
  /** Abort the transfer of one file, or delete what it already became on the server. */
  const cancelUpload = (key: string): void => {
    const control = uploadControl.current.get(key) ?? { cancelled: false };
    control.cancelled = true;
    uploadControl.current.set(key, control);
    control.abort?.abort();
    setUploads((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    // Already created on the server (half uploaded, or transcoding): delete it, which also
    // stops a running transcode. If the version is still being created, handleFiles deletes it
    // the moment it exists.
    if (control.versionId && projectId) {
      const id = projectId;
      void api
        .deleteVersion(control.versionId)
        .catch(() => undefined)
        .then(() => refreshProject(id));
    }
  };

  const handleFiles = async (dropped: DroppedFile[]): Promise<void> => {
    if (!projectId) return;

    interface Pending {
      key: string;
      filename: string;
      versionId: string;
      shotId: string;
    }

    // A folder drop suggests the review name. Only picks it up when every file came from the
    // same folder — a drop that mixes several folders (or bare files) has no obvious name.
    const folder = commonFolder(dropped.filter((d) => VIDEO_EXT.test(d.file.name)));
    if (folder) setSuggestedReviewName(folder);

    // ---- phase 1: get every file onto the server, back to back.
    const pending: Pending[] = [];
    // Clips the drop covered but did not upload, because they are already here. They belong in
    // the review just as much as the new ones — the folder is the selection, not the diff.
    const alreadyHere: string[] = [];

    // Every file shows up at once, so any of them can be cancelled before its turn comes.
    for (const { file } of dropped) {
      if (!VIDEO_EXT.test(file.name)) continue;
      const key = `${file.name}-${file.size}`;
      uploadControl.current.set(key, { cancelled: false });
      setUploads((prev) => ({ ...prev, [key]: { filename: file.name, progress: 0, state: 'queued' } }));
    }

    for (const { file } of dropped) {
      if (!VIDEO_EXT.test(file.name)) continue;
      const key = `${file.name}-${file.size}`;
      const control = uploadControl.current.get(key) ?? { cancelled: false };
      if (control.cancelled) continue;
      const { shotName, versionName } = parseShotVersion(file.name);

      // Re-dropping a folder is normal — it is how you add the two clips that were missing.
      // Uploading the rest again would be pointless and would overwrite nothing anyway, since
      // the server refuses a version name that exists.
      const existing = versionName
        ? versions.find((v) => v.shotName === shotName && v.name === versionName)
        : undefined;
      if (existing) {
        alreadyHere.push(existing.shotId);
        setUploads((prev) => ({
          ...prev,
          [key]: {
            filename: file.name,
            progress: 1,
            state: 'skipped',
            // Said plainly: a re-export under an existing version name is NOT ingested, and
            // believing otherwise would mean reviewing an old picture.
            message: 'already in the project — not uploaded again',
          },
        }));
        continue;
      }

      try {
        setUploads((prev) => ({ ...prev, [key]: { filename: file.name, progress: 0, state: 'uploading' } }));
        const shot = await api.createShot(projectId, shotName);
        const version = await createVersionForUpload(shot.id, file.name, versionName);
        control.versionId = version.id;
        if (control.cancelled) {
          // Cancelled while the version was being created: it exists now, so remove it.
          await api.deleteVersion(version.id).catch(() => undefined);
          continue;
        }
        control.abort = new AbortController();
        await uploadSource(
          version.id,
          file,
          (fraction) =>
            setUploads((prev) =>
              key in prev
                ? { ...prev, [key]: { filename: file.name, progress: fraction, state: 'uploading' } }
                : prev,
            ),
          control.abort.signal,
        );
        if (control.cancelled) continue;
        setUploads((prev) => ({
          ...prev,
          [key]: { filename: file.name, progress: 0, state: 'transcoding' },
        }));
        pending.push({ key, filename: file.name, versionId: version.id, shotId: shot.id });
      } catch (err) {
        // A cancelled file is simply gone from the list; cancelUpload already cleaned up.
        if (control.cancelled) continue;
        setUploads((prev) => ({
          ...prev,
          [key]: {
            filename: file.name,
            progress: 0,
            state: 'error',
            message: err instanceof Error ? err.message : String(err),
          },
        }));
      }
    }

    if (pending.length === 0 && alreadyHere.length === 0) return;

    // The clips already exist (still transcoding), so show them right away.
    await refreshProject(projectId);
    // What the drop covered, not what it changed: the clips that were already here start the
    // selection, and the new ones join as they land.
    setSelected(new Set(alreadyHere));

    if (pending.length === 0) return;

    // ---- phase 2: watch the whole queue drain, whatever order ffmpeg works through it.
    const waiting = new Map(pending.map((p) => [p.versionId, p]));
    const fail = (p: Pending, message: string): void => {
      waiting.delete(p.versionId);
      setUploads((prev) => ({
        ...prev,
        [p.key]: { filename: p.filename, progress: 0, state: 'error', message },
      }));
    };

    while (waiting.size > 0) {
      for (const p of [...waiting.values()]) {
        if (uploadControl.current.get(p.key)?.cancelled) {
          waiting.delete(p.versionId);
          continue;
        }
        try {
          const res = await api.getVersion(p.versionId);
          if (res.version.ingestState === 'ready') {
            waiting.delete(p.versionId);
            setUploads((prev) => ({
              ...prev,
              [p.key]: { filename: p.filename, progress: 1, state: 'done' },
            }));
            await refreshProject(projectId);
            // Pre-select as clips land, so a review link is one click away.
            setSelected((prev) => new Set(prev).add(p.shotId));
          } else if (res.version.ingestState === 'failed') {
            fail(p, res.version.ingestError ?? 'Transcoding failed');
          } else {
            setUploads((prev) => ({
              ...prev,
              [p.key]: {
                filename: p.filename,
                progress: res.job?.progress ?? 0,
                state: 'transcoding',
                message: res.job?.step ?? undefined,
              },
            }));
          }
        } catch (err) {
          fail(p, err instanceof Error ? err.message : String(err));
        }
      }
      if (waiting.size > 0) await new Promise((r) => setTimeout(r, 1500));
    }
  };

  const createPlaylist = async (): Promise<void> => {
    if (!projectId || selected.size === 0) return;
    const defaultName = suggestedReviewName ?? `${todayDdMmYyyy()}_VFX-review`;
    const name = prompt('Playlist name?', defaultName);
    if (!name) return;
    try {
      // Include every version of each selected clip so the review can switch between them;
      // keep on-screen order (shots in order, versions ascending).
      const ordered = versions.filter((v) => selected.has(v.shotId)).map((v) => v.id);
      const res = await api.createPlaylist({ projectId, name, versionIds: ordered });
      const session = await api.createSession({ playlistId: res.playlist.id, name });
      setSelected(new Set());
      setEditingReview(null);
      // The folder name only counted for this one review; a later manual click gets the date default.
      setSuggestedReviewName(null);
      await refreshProject(projectId);
      const url = `${location.origin}/s/${session.session.id}`;
      await navigator.clipboard?.writeText(url).catch(() => undefined);
      alert(`Review link created (copied to clipboard):\n${url}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Add the selected clips to every checked review. Adds all versions of each clip (like
  // creating a review does); INSERT OR IGNORE on the server skips ones already in the review.

  // Clicking a review pre-checks the clips it already contains, so unchecking one and hitting
  // "Update review" removes it — the same checkbox list doubles as an editor for membership.
  const startEditingReview = async (p: Playlist): Promise<void> => {
    try {
      const res = await api.getPlaylist(p.id);
      setEditingReview({ id: p.id, name: p.name, items: res.items });
      setSelected(new Set(res.items.map((it) => it.version.shotId)));
      } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const cancelReviewEdit = (): void => {
    setEditingReview(null);
    setSelected(new Set());
  };

  const startLongPress = (p: Playlist): void => {
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setRenamingReviewId(p.id);
      setRenameValue(p.name);
    }, 550);
  };

  const clearLongPress = (): void => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };

  const cancelRename = (): void => {
    setRenamingReviewId(null);
    setRenameValue('');
  };

  const saveRename = async (id: string): Promise<void> => {
    const name = renameValue.trim();
    setRenamingReviewId(null);
    if (!name) return;
    try {
      await api.setPlaylistSettings(id, { name });
      setPlaylists((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Diffs the checked clips against what the review had when editing started: newly checked
  // shots are added (their current version), unchecked ones have every one of their items
  // removed — "clip" means the whole shot here, same as everywhere else in this table.
  const applyReviewEdits = async (): Promise<void> => {
    if (!editingReview) return;
    const originalShotIds = new Set(editingReview.items.map((it) => it.version.shotId));
    const toRemove = editingReview.items.filter((it) => !selected.has(it.version.shotId));
    const addVersionIds = versions
      .filter((v) => selected.has(v.shotId) && !originalShotIds.has(v.shotId))
      .map((v) => v.id);
    try {
      for (const it of toRemove) await api.removePlaylistItem(editingReview.id, it.itemId);
      if (addVersionIds.length > 0) await api.addPlaylistItems(editingReview.id, addVersionIds);
      const name = editingReview.name;
      setEditingReview(null);
      setSelected(new Set());
      if (projectId) await refreshProject(projectId);
      alert(`Review “${name}” updated: added ${addVersionIds.length}, removed ${toRemove.length}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const openSessionFor = async (playlistId: string, name: string): Promise<void> => {
    try {
      const session = await api.createSession({ playlistId, name });
      location.href = `/s/${session.session.id}`;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  /**
   * Delete files under DATA_DIR that no clip references any more — leftovers from earlier
   * deletes and from uploads that never finished. The server decides what qualifies; this
   * only asks.
   */
  const cleanupStorage = async (): Promise<void> => {
    if (!storage || storage.orphans.count === 0) return;
    if (
      !confirm(
        `Delete ${storage.orphans.count} file(s) that no clip references any more ` +
          `(${formatBytes(storage.orphans.bytes)})? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      const res = await api.cleanupStorage();
      await loadStorage();
      alert(`Removed ${res.removed} file(s), freed ${formatBytes(res.bytes)}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const applyToken = (): void => {
    setAdminToken(token.trim());
    void loadProjects();
  };

  const unlock = (): void => {
    setLockTried(true);
    applyToken();
  };

  // Gate: the whole management surface requires the API key.
  if (locked) {
    return (
      <div className="centered">
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            unlock();
          }}
        >
          <h1>OpenReview</h1>
          <p className="muted">Enter your API key to manage projects and uploads.</p>
          <label>
            API key
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoFocus
              required
            />
          </label>
          {lockTried ? <p className="error">Key not accepted.</p> : null}
          <button type="submit" className="primary" disabled={!token.trim()}>
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin">
      <header className="admin__bar">
        {currentProject ? (
          <>
            <button type="button" onClick={backToList}>
              ← Projects
            </button>
            <strong>{currentProject.name}</strong>
          </>
        ) : (
          <strong>OpenReview</strong>
        )}
        <span className="spacer" />
        {authRequired ? (
          <label className="admin__token">
            API token
            <input
              type="password"
              value={token}
              placeholder="required"
              onChange={(e) => setToken(e.target.value)}
              // Stored once you are done typing, not on every keystroke: a half-typed token
              // in localStorage is a wrong token that survives the next reload.
              onBlur={applyToken}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyToken();
              }}
            />
          </label>
        ) : (
          <span className="muted">open dev mode</span>
        )}
      </header>

      {error ? (
        <div className="banner banner--error" onClick={() => setError(null)}>
          {error}
        </div>
      ) : null}

      {!projectId ? (
        <div className="admin__body">
          <section className="admin__section">
            <h2>
              Projects
              <span className="spacer" />
              <ViewToggle view={projectView} onChange={changeProjectView} label="Project view" />
            </h2>
            {projectView === 'list' ? (
              <ul className="project-list">
                {projects.map((p) => (
                  <li
                    key={p.id}
                    className="project-row"
                    onClick={() => void openProject(p.id)}
                    title="Drop an image to set the cover"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files?.[0];
                      if (file && file.type.startsWith('image/')) void setThumbnail(p.id, file);
                    }}
                  >
                    <span className="project-row__thumb">
                      {p.thumbnail ? <img src={p.thumbnail} alt="" /> : <span>{p.code}</span>}
                    </span>
                    <span className="project-row__name">{p.name}</span>
                    <span className="project-row__size">
                      {storage ? formatBytes(storage.projects[p.id] ?? 0) : '…'}
                    </span>
                    <button
                      type="button"
                      className="icon-delete"
                      title="Delete project"
                      aria-label={`Delete project ${p.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        void deleteProject(p);
                      }}
                    >
                      ×
                    </button>
                  </li>
                ))}
                <li>
                  <button type="button" className="project-row project-row--new" onClick={() => void createProject()}>
                    + New project
                  </button>
                </li>
              </ul>
            ) : (
              <div className="projects">
                {projects.map((p) => (
                  <div
                    key={p.id}
                    className="project-card"
                    title="Drop an image to set the cover"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files?.[0];
                      if (file && file.type.startsWith('image/')) void setThumbnail(p.id, file);
                    }}
                  >
                    <button
                      type="button"
                      className="project-card__delete"
                      title="Delete project"
                      onClick={() => void deleteProject(p)}
                    >
                      ×
                    </button>
                    <button type="button" className="project-card__thumb" onClick={() => void openProject(p.id)}>
                      {p.thumbnail ? (
                        <img src={p.thumbnail} alt="" />
                      ) : (
                        <span className="project-card__placeholder">{p.code}</span>
                      )}
                    </button>
                    <span className="project-card__name">{p.name}</span>
                  </div>
                ))}
                <button
                  type="button"
                  className="project-card project-card--new"
                  onClick={() => void createProject()}
                >
                  + New project
                </button>
              </div>
            )}
          </section>

          {storage ? (
            <section className="admin__section">
              <h2>
                Storage
                {storage.orphans.count > 0 ? (
                  <button type="button" className="danger" onClick={() => void cleanupStorage()}>
                    Clean up {storage.orphans.count} unreferenced file
                    {storage.orphans.count === 1 ? '' : 's'} · {formatBytes(storage.orphans.bytes)}
                  </button>
                ) : null}
              </h2>
              <ul className="storage">
                <li>
                  <span>Originals</span>
                  <strong>{formatBytes(storage.usage.source)}</strong>
                </li>
                <li>
                  <span>Transcodes</span>
                  <strong>{formatBytes(storage.usage.media)}</strong>
                </li>
                <li>
                  <span>Uploads in flight</span>
                  <strong>{formatBytes(storage.usage.tmp)}</strong>
                </li>
                <li>
                  <span>Database</span>
                  <strong>{formatBytes(storage.usage.database)}</strong>
                </li>
                <li className="storage__total">
                  <span>Used</span>
                  <strong>{formatBytes(storage.usage.total)}</strong>
                </li>
                <li className="storage__total">
                  <span>Free on disk</span>
                  <strong>{formatBytes(storage.usage.free)}</strong>
                </li>
              </ul>
              <p className="muted">
                {storage.counts.versions} version{storage.counts.versions === 1 ? '' : 's'} in{' '}
                {storage.counts.projects} project{storage.counts.projects === 1 ? '' : 's'} ·{' '}
                <code>{storage.dataDir}</code>
              </p>
              {storage.orphans.count > 0 ? (
                <details className="storage__orphans">
                  <summary>
                    {storage.orphans.count} file{storage.orphans.count === 1 ? '' : 's'} no clip
                    points at any more
                  </summary>
                  <ul className="list">
                    {storage.orphans.items.map((o) => (
                      <li key={o.path}>
                        <span>
                          <code>{o.path}</code>
                        </span>
                        <span className="muted">
                          {formatBytes(o.bytes)} · {o.reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </section>
          ) : null}
        </div>
      ) : (
        <div className="admin__body">
          <section className="admin__section">
            <h2>Upload clips</h2>
            <div
              className="dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                void filesFromDrop(e.dataTransfer).then((files) => handleFiles(files));
              }}
              // Keeping the browser from opening the file when the drop misses the zone.
              onDragEnter={(e) => e.preventDefault()}
              onClick={() => fileInput.current?.click()}
            >
              <p>Drag ProRes, DNxHR, MP4, PNG or JPG here — files or whole folders</p>
              <p className="muted">
                Shot and version are derived from the filename (FRW_145_010_v02.mov → shot
                FRW_145_010, version v02)
              </p>
              <button
                type="button"
                className="dropzone__folder"
                onClick={(e) => {
                  e.stopPropagation();
                  folderInput.current?.click();
                }}
              >
                Choose folder…
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept="video/*,image/png,image/jpeg,.mov,.mp4,.mxf,.mkv,.png,.jpg,.jpeg"
                hidden
                onChange={(e) => e.target.files && void handleFiles(fromFileList(e.target.files))}
              />
              <input
                ref={folderInput}
                type="file"
                multiple
                hidden
                // webkitdirectory is not in React's typed attributes, but every target browser supports it.
                {...({ webkitdirectory: '' } as Record<string, string>)}
                onChange={(e) => e.target.files && void handleFiles(fromFileList(e.target.files))}
              />
            </div>

            {Object.entries(uploads).map(([key, u]) => (
              <div key={key} className={`upload upload--${u.state}`}>
                <span className="upload__name">{u.filename}</span>
                <div className="upload__bar">
                  <div style={{ width: `${u.progress * 100}%` }} />
                </div>
                <span className="upload__state">
                  {u.state === 'queued' && 'waiting'}
                  {u.state === 'uploading' && `${Math.round(u.progress * 100)} %`}
                  {u.state === 'transcoding' && `transcoding ${Math.round(u.progress * 100)} % ${u.message ?? ''}`}
                  {u.state === 'done' && 'done'}
                  {u.state === 'skipped' && (u.message ?? 'already here')}
                  {u.state === 'error' && (u.message ?? 'error')}
                </span>
                {u.state === 'queued' || u.state === 'uploading' || u.state === 'transcoding' ? (
                  <button
                    type="button"
                    className="icon-delete"
                    title={
                      u.state === 'transcoding'
                        ? 'Cancel — deletes this clip from the project again'
                        : 'Cancel this upload'
                    }
                    aria-label={`Cancel upload of ${u.filename}`}
                    onClick={() => cancelUpload(key)}
                  >
                    ×
                  </button>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </section>

          <section className="admin__section">
            <h2>
              Reviews
              <button
                type="button"
                className="danger"
                disabled={!editingReview}
                onClick={() => void deleteOpenReview()}
                title={
                  editingReview
                    ? `Delete the review “${editingReview.name}”`
                    : 'Click a review first — Delete acts on the open one'
                }
              >
                Delete
              </button>
            </h2>
            <ul className="list list--grid">
              {playlists.map((p) => (
                <li
                  key={p.id}
                  className={editingReview?.id === p.id ? 'is-editing' : ''}
                  // Clicking the open one closes it again. The way out used to be the Cancel
                  // button down in the Clips header, which is nowhere near the row you clicked.
                  onClick={() =>
                    editingReview?.id === p.id ? cancelReviewEdit() : void startEditingReview(p)
                  }
                  title={
                    editingReview?.id === p.id
                      ? 'Click to close this review again'
                      : 'Click to check the clips already in this review below'
                  }
                >
                  <span>
                    {renamingReviewId === p.id ? (
                      <input
                        autoFocus
                        className="list__rename"
                        value={renameValue}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => void saveRename(p.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveRename(p.id);
                          else if (e.key === 'Escape') cancelRename();
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="list__link"
                        onMouseDown={() => startLongPress(p)}
                        onMouseUp={clearLongPress}
                        onMouseLeave={clearLongPress}
                        onTouchStart={() => startLongPress(p)}
                        onTouchEnd={clearLongPress}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (longPressFired.current) return;
                          void openSessionFor(p.id, p.name);
                        }}
                        title="Click to open — press and hold to rename"
                      >
                        {p.name}
                      </button>
                    )}{' '}
                    <span className="muted">{p.shotCount} shots</span>
                  </span>
                  <button
                    type="button"
                    className={`icon-toggle${p.guest_downloads ? ' active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void toggleReviewDownloads(p);
                    }}
                    title={
                      p.guest_downloads
                        ? 'Reviewers may download these clips. Click to switch downloads off for this review.'
                        : 'Reviewers cannot download. Click to allow downloads for this review.'
                    }
                    aria-label={p.guest_downloads ? 'Downloads on' : 'Downloads off'}
                  >
                    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M12 3v10m0 0l-4-4m4 4l4-4"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
                      {/* A struck-through arrow reads as "off" without needing a second icon. */}
                      {p.guest_downloads ? null : (
                        <path d="M4 20L20 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      )}
                    </svg>
                  </button>
                </li>
              ))}
              {playlists.length === 0 ? <li className="muted">No reviews yet.</li> : null}
            </ul>
          </section>


          <section className="admin__section">
            <h2>
              <input
                type="checkbox"
                checked={shotGroups.size > 0 && selected.size === shotGroups.size}
                title="Select all clips"
                aria-label="Select all clips"
                onChange={(e) => {
                  if (e.target.checked) {
                    if (!confirm(`Select all ${shotGroups.size} clips?`)) return;
                    setSelected(new Set(shotGroups.keys()));
                  } else {
                    setSelected(new Set());
                  }
                }}
              />
              Clips ({shotGroups.size})
              <ViewToggle view={clipView} onChange={changeClipView} label="Clip view" />
              <span className="view-toggle" role="group" aria-label="Clip details">
                <button
                  type="button"
                  className={showVersions ? 'active' : ''}
                  aria-pressed={showVersions}
                  aria-label="Show versions"
                  title={showVersions ? 'Hide versions' : 'Show versions'}
                  onClick={toggleShowVersions}
                >
                  {/* Stacked sheets: one shot, several versions. */}
                  <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 3l8 4-8 4-8-4z" />
                    <path d="M4 11.5l8 4 8-4" />
                    <path d="M4 16l8 4 8-4" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={showStatus ? 'active' : ''}
                  aria-pressed={showStatus}
                  aria-label="Show status"
                  title={showStatus ? 'Hide status' : 'Show status'}
                  onClick={toggleShowStatus}
                >
                  {/* A ring with a dot: the status dot itself. */}
                  <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="8" />
                    <circle className="icon__fill" cx="12" cy="12" r="3.5" />
                  </svg>
                </button>
              </span>
              {editingReview ? (
                <>
                  <span className="muted">
                    Editing review “{editingReview.name}” — check/uncheck clips below, then
                  </span>
                  <button type="button" className="primary" onClick={() => void applyReviewEdits()}>
                    Update review
                  </button>
                  <button type="button" onClick={cancelReviewEdit}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="primary"
                    disabled={selected.size === 0}
                    onClick={() => void createPlaylist()}
                  >
                    Review link from {selected.size} clips
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={selected.size === 0}
                    onClick={() => void deleteSelectedVersions()}
                    title="Delete the shown version of each selected clip"
                  >
                    Delete
                  </button>
                </>
              )}
            </h2>

            <div className={clipView === 'grid' ? 'clip-grid' : 'clip-list'}>
              {Array.from(shotGroups.entries()).map(([shotId, vs]) => {
                const v = chosenVersionOf(shotId) ?? vs[vs.length - 1]!;
                const ready = v.ingestState === 'ready';
                // Status is per shot, not per shown version: approved if any version was
                // approved, tagged with the highest version that carries the verdict.
                const verdict = aggregateStatus(vs);
                // With a sheet, the shot's word in the sheet's vocabulary is the status; without
                // one, the plain verdict.
                const state = shotState(verdict.status, v.sheetStage, v.sheetStageColor);
                // Which version carries a verdict, e.g. "Approved · v003".
                const stateTitle =
                  verdict.version && verdict.status !== 'pending'
                    ? `${state.label} · ${verdict.version.name}`
                    : state.label;

                // The words on offer: the sheet's own dropdown, else the standard set — plus the
                // shot's current word if it is not among them, so the select can show it.
                const words = sheet
                  ? sheet.options.length > 0
                    ? sheet.options
                    : Object.values(SHOT_STATES).map((st) => st.label)
                  : [];
                const currentWord =
                  v.sheetStage === null
                    ? ''
                    : (words.find((w) => w.toLowerCase() === v.sheetStage!.toLowerCase()) ?? v.sheetStage);

                // A coloured dot instead of the word; the word is the hover tooltip. The select
                // lies invisibly over the dot, so clicking it still changes the status.
                const badge = !showStatus ? null : (
                  <span
                    className="status-dot"
                    style={{ '--dot-bg': state.bg } as React.CSSProperties}
                    title={stateTitle}
                  >
                    {sheet ? (
                      <select
                        value={currentWord}
                        aria-label={`Status: ${state.label}`}
                        // Picking a status is not picking the clip, same as the version dropdown.
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => void changeShotStage(shotId, e.target.value)}
                        title={stateTitle}
                      >
                        {currentWord === '' ? <option value="">—</option> : null}
                        {words.includes(currentWord) || currentWord === '' ? null : (
                          <option value={currentWord}>{currentWord}</option>
                        )}
                        {words.map((w) => (
                          <option key={w} value={w}>
                            {w}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={verdict.status}
                        aria-label={`Status: ${state.label}`}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => void changeShotStatus(verdict.version ?? v, e.target.value as VersionStatus)}
                        title={stateTitle}
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    )}
                  </span>
                );

                const versionPicker = !showVersions ? null : vs.length > 1 ? (
                    <select
                      className="version-pill"
                      value={v.id}
                      // Picking a version is not picking the clip: the card toggles on click.
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setChosenVersion((prev) => ({ ...prev, [shotId]: e.target.value }))}
                      title={`${vs.length} versions`}
                    >
                      {[...vs].reverse().map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="version-pill">{v.name}</span>
                  );

                // Ingest state used to be its own table column; folded onto the thumbnail
                // instead of taking a whole column now that most clips just say "ready".
                const ingestBadge = !ready ? (
                  <span
                    className={`clip-card__ingest${v.ingestState === 'failed' ? ' error' : ''}`}
                    title={v.ingestState === 'failed' ? (v.ingestError ?? '') : undefined}
                  >
                    {v.ingestState}
                  </span>
                ) : (v.media.fullHeight ?? 0) > 1080 && !v.media.proxy ? (
                  <span className="clip-card__ingest" title="The HD fallback is still encoding">
                    proxy…
                  </span>
                ) : null;

                const onToggle = (): void => toggleClip(shotId, ready);
                const onToggleKey = (e: React.KeyboardEvent): void => {
                  if (e.key !== ' ' && e.key !== 'Enter') return;
                  e.preventDefault(); // Space would scroll the list instead of picking a clip.
                  toggleClip(shotId, ready);
                };

                if (clipView === 'list') {
                  return (
                    <div
                      key={shotId}
                      className={`clip-row${selected.has(shotId) ? ' is-selected' : ''}${ready ? '' : ' clip-row--busy'}`}
                      role="checkbox"
                      aria-checked={selected.has(shotId)}
                      aria-disabled={!ready}
                      aria-label={v.shotName}
                      tabIndex={ready ? 0 : -1}
                      onClick={onToggle}
                      onKeyDown={onToggleKey}
                    >
                      <div className="clip-row__thumb">
                        {v.media.poster ? (
                          <img src={v.media.poster} alt="" loading="lazy" />
                        ) : (
                          <span className="clip-row__thumb-empty" aria-hidden="true" />
                        )}
                      </div>
                      <span className="clip-row__name">{v.shotName}</span>
                      {versionPicker}
                      {ingestBadge}
                      {badge}
                    </div>
                  );
                }

                return (
                  <div
                    key={shotId}
                    className={`clip-card${selected.has(shotId) ? ' is-selected' : ''}${
                      ready ? '' : ' clip-card--busy'
                    }`}
                    role="checkbox"
                    aria-checked={selected.has(shotId)}
                    aria-disabled={!ready}
                    aria-label={v.shotName}
                    tabIndex={ready ? 0 : -1}
                    onClick={onToggle}
                    onKeyDown={onToggleKey}
                  >
                    <div className="clip-card__head">{badge}</div>

                    <div className="clip-card__thumb">
                      {v.media.poster ? (
                        <img src={v.media.poster} alt="" loading="lazy" />
                      ) : (
                        <span className="clip-card__thumb-empty" aria-hidden="true" />
                      )}
                      {ingestBadge}
                    </div>

                    <div className="clip-card__foot">
                      <span className="clip-card__name">{v.shotName}</span>
                      {versionPicker}
                    </div>
                  </div>
                );
              })}
            </div>
            {shotGroups.size === 0 ? <p className="muted">No clips in this project yet.</p> : null}
          </section>

          <SheetPanel projectId={projectId} />
        </div>
      )}
    </div>
  );
}
